import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import * as authApi from '../api/auth'

type Step = 'auid' | 'email' | 'password' | 'success'

export function ClaimAccount() {
  const [step, setStep] = useState<Step>('auid')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Form state
  const [auid, setAuid] = useState('')
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null)
  const [hasEmail, setHasEmail] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    if (!auid.trim()) return

    setError('')
    setLoading(true)
    try {
      const result = await authApi.claimVerify({ auid: auid.trim() })

      if (!result.memberExists) {
        setError('No AU member found with that ID. Please check your AU ID and try again.')
        setLoading(false)
        return
      }

      setMaskedEmail(result.emailMasked)
      setHasEmail(result.hasEmail)

      if (result.hasEmail) {
        setEmail('')
      }

      setStep('email')
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { error?: string } } })?.response
      if (resp?.status === 409) {
        setError(resp.data?.error || 'This AU ID already has a login account.')
      } else {
        setError('Unable to verify your AU ID. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  function handleEmailContinue(e: React.FormEvent) {
    e.preventDefault()

    // With no address on file there is nothing to confirm against, and the
    // backend will refuse the claim -- so this step is a dead end by design.
    if (!hasEmail) {
      setError('Please contact the AU Office to have your email address added before signing up.')
      return
    }

    // The address is now always required: claim/complete matches it against the
    // one on file, so an empty value can never succeed.
    if (!email.trim()) {
      setError('Please type the email address we have on file for your AU ID.')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.')
      return
    }

    setError('')
    setStep('password')
  }

  async function handleComplete(e: React.FormEvent) {
    e.preventDefault()

    if (!password) {
      setError('Please enter a password.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setError('')
    setLoading(true)
    try {
      const emailToUse = email.trim() || undefined
      await authApi.claimComplete({
        auid: auid.trim(),
        email: emailToUse || '',
        password,
      })
      setStep('success')
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Unable to create your account. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Claim Your Account - ARPU</title>
      </Helmet>

      <div className="max-w-md mx-auto mt-12">
        <h1 className="text-2xl font-heading font-bold text-text mb-2 text-center">
          Claim Your Account
        </h1>
        <p className="text-text-light text-center mb-6">
          Already an AU member? Set up your online account.
        </p>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {(['auid', 'email', 'password', 'success'] as const).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === s
                  ? 'bg-primary text-text-inverse'
                  : (['auid', 'email', 'password', 'success'].indexOf(step) > i)
                    ? 'bg-success text-text-inverse'
                    : 'bg-border text-text-muted'
              }`}>
                {i + 1}
              </div>
              {i < 3 && <div className="w-6 h-0.5 bg-border" />}
            </div>
          ))}
        </div>

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Enter AU ID */}
        {step === 'auid' && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label htmlFor="auid" className="block text-sm font-medium text-text mb-1">
                AU ID Number
              </label>
              <input
                id="auid"
                type="text"
                value={auid}
                onChange={(e) => setAuid(e.target.value)}
                placeholder="e.g. AU012345 or 012345"
                required
                className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
              <p className="text-xs text-text-muted mt-1">
                Your AU ID is on your membership card or band order paperwork.
              </p>
            </div>
            <button
              type="submit"
              disabled={loading || !auid.trim()}
              className="w-full py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Look Up My AU ID'}
            </button>
          </form>
        )}

        {/* Step 2: Verify/enter email */}
        {step === 'email' && (
          <form onSubmit={handleEmailContinue} className="space-y-4">
            {/*
              An AU ID is not a secret -- it is printed in published race results
              -- so claiming an account now requires proving you can already
              receive mail at the address on file. See the email-match check in
              api/v1/routes/auth.php:handleClaimComplete(). The two branches below
              must match that contract exactly: this step previously invited
              members to type a NEW address, which the backend now refuses.
            */}
            {hasEmail ? (
              <div>
                <p className="text-text-light mb-3">
                  We found your AU membership. The email on file is:
                </p>
                <p className="font-medium text-text mb-4 bg-surface-alt border border-border rounded-md px-4 py-3">
                  {maskedEmail}
                </p>
                <p className="text-sm text-text-muted mb-3">
                  To confirm this account is yours, type that address in full below.
                  It has to match the one we have on file. If you no longer use it,
                  call the AU Office at 405-848-5801 and we will update it for you.
                </p>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-text mb-1">
                    Confirm the email address on file
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                </div>
              </div>
            ) : (
              <div>
                <p className="text-text-light mb-3">
                  We found your AU membership, but there is no email address on file
                  for it.
                </p>
                <p className="text-sm text-text-muted mb-3">
                  Because an AU ID appears in published race results, we cannot set
                  up an online account without an email address already on record to
                  confirm it is yours.
                </p>
                <p className="text-sm text-text-muted">
                  Please call the AU Office at{' '}
                  <span className="font-medium text-text">405-848-5801</span> or email{' '}
                  <a href="mailto:arpuoffice@pigeon.org" className="text-primary hover:underline">
                    arpuoffice@pigeon.org
                  </a>{' '}
                  and a staff member will add your email address. Once it is on file,
                  come back here and you can finish signing up.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setStep('auid'); setError('') }}
                className="px-4 py-2.5 border border-border hover:bg-surface-hover text-text-light font-medium rounded-md transition-colors"
              >
                Back
              </button>
              {/* No Continue when there is no email on file -- the backend cannot
                  complete the claim, so offering the button would only produce a
                  dead end. */}
              {hasEmail && (
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors"
                >
                  Continue
                </button>
              )}
            </div>
          </form>
        )}

        {/* Step 3: Set password */}
        {step === 'password' && (
          <form onSubmit={handleComplete} className="space-y-4">
            <p className="text-text-light mb-2">
              Create a password for your online account.
            </p>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
              <p className="text-xs text-text-muted mt-1">Minimum 8 characters.</p>
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-text mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setStep('email'); setError('') }}
                className="px-4 py-2.5 border border-border hover:bg-surface-hover text-text-light font-medium rounded-md transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>
        )}

        {/* Step 4: Success */}
        {step === 'success' && (
          <div className="text-center">
            <div className="bg-success/10 border border-success/30 rounded-lg p-8">
              <h2 className="text-xl font-heading font-bold text-success mb-3">
                Account Created
              </h2>
              <p className="text-text-light mb-6">
                Your online account has been set up. You can now log in with your email and password.
              </p>
              <Link
                to="/login"
                className="inline-block px-6 py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors"
              >
                Log In
              </Link>
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-text-muted space-y-2">
          <p>
            <Link to="/login" className="text-primary hover:underline">
              Already have an account? Log in
            </Link>
          </p>
          <p>
            Not an AU member?{' '}
            <Link to="/join" className="text-primary hover:underline">
              Join online
            </Link>
          </p>
        </div>
      </div>
    </>
  )
}
